import { formatInstantDateToPtBR } from '@/shared/lib';
import type { AuditUserResponse } from '@/shared/lib';
import { resolveDirectoryItemLabelParts } from './directoryItemLabel';
import { resolveIconId } from '@/shared/ui/module-icons';
import {
  type DirectoryItemResponse,
  type FolderNodeResponse,
  type ModuleTreeSortField,
  type ModuleTreeSortOrder,
  type ModuleWithFoldersResponse,
} from '../api/directories.api';
import type { DirectoryNodeKind, DirectoryNodeDetails, DirectoryTreeNode } from './types';

const ITEM_TYPE_TO_KIND: Record<DirectoryItemResponse['itemType'], DirectoryNodeKind> = {
  DOCUMENT: 'document',
  RECORD: 'record',
  MATRIX: 'matrix',
  GENERATED_RECORD: 'generatedRecord',
};

interface AuditFields {
  createdAt: string | null;
  createdBy: AuditUserResponse;
  savedInDirectoryBy: AuditUserResponse;
}

function buildDetails(title: string, audit: AuditFields): DirectoryNodeDetails {
  return {
    title,
    createdAt: audit.createdAt,
    createdAtLabel: formatInstantDateToPtBR(audit.createdAt),
    createdBy: audit.createdBy?.name ?? '-',
    savedInDirectoryBy: audit.savedInDirectoryBy?.name ?? '-',
  };
}

function mapDirectoryItemToTreeNode(
  item: DirectoryItemResponse,
  ownerNodeId: string
): DirectoryTreeNode {
  const nodeId =
    item.linkId !== null
      ? `item-${item.linkId}`
      : `${ownerNodeId}:derived-${item.itemType}-${item.itemId}`;
  const { label, prefix, title } = resolveDirectoryItemLabelParts(item.name, item.prefix);

  return {
    id: nodeId,
    name: label,
    kind: ITEM_TYPE_TO_KIND[item.itemType],
    prefix,
    title,
    linkId: item.linkId,
    ...(item.derived === true ? { derived: true as const } : {}),
    itemId: item.itemId,
    details: {
      ...buildDetails(label, item),
      ...(item.archived === true ? { archived: true } : {}),
    },
  };
}

function getTreeNodeCreatedAtTime(node: DirectoryTreeNode): number | null {
  const createdAt = node.details?.createdAt;
  if (!createdAt) {
    return null;
  }
  const time = new Date(createdAt).getTime();
  return Number.isNaN(time) ? null : time;
}

function compareTreeNodes(
  a: DirectoryTreeNode,
  b: DirectoryTreeNode,
  sort: ModuleTreeSortField,
  order: ModuleTreeSortOrder
): number {
  const directionMultiplier = order === 'asc' ? 1 : -1;

  if (sort === 'name') {
    return directionMultiplier * a.name.localeCompare(b.name, 'pt-BR');
  }

  const timeA = getTreeNodeCreatedAtTime(a);
  const timeB = getTreeNodeCreatedAtTime(b);

  if (timeA === null && timeB === null) {
    return a.name.localeCompare(b.name, 'pt-BR');
  }
  if (timeA === null) {
    return 1;
  }
  if (timeB === null) {
    return -1;
  }

  return directionMultiplier * (timeA - timeB);
}

function sortTreeNodes(
  nodes: DirectoryTreeNode[],
  sort?: ModuleTreeSortField,
  order?: ModuleTreeSortOrder
): DirectoryTreeNode[] {
  if (!sort) {
    return nodes;
  }

  const resolvedOrder = order ?? 'asc';
  return [...nodes].sort((a, b) => compareTreeNodes(a, b, sort, resolvedOrder));
}

function mapFolderNodeToTreeNode(
  folder: FolderNodeResponse,
  moduleId: number,
  sort?: ModuleTreeSortField,
  order?: ModuleTreeSortOrder
): DirectoryTreeNode {
  const nodeId = `folder-${folder.id}`;

  return {
    id: nodeId,
    name: folder.name,
    kind: 'folder',
    moduleId,
    folderType: folder.type,
    ...(folder.contentType ? { folderContentType: folder.contentType } : {}),
    prefixId: folder.prefixId ?? undefined,
    ...(folder.matrixPlacementLocked === true ? { matrixPlacementLocked: true as const } : {}),
    ...(folder.matrixPlacementMoveLocked === true
      ? { matrixPlacementMoveLocked: true as const }
      : {}),
    ...(folder.matrixPlacementDeleteLock
      ? { matrixPlacementDeleteLock: folder.matrixPlacementDeleteLock }
      : {}),
    details: buildDetails(folder.name, folder),
    children: sortTreeNodes(
      [
        ...folder.children.map((child) => mapFolderNodeToTreeNode(child, moduleId, sort, order)),
        ...(folder.items ?? []).map((item) => mapDirectoryItemToTreeNode(item, nodeId)),
      ],
      sort,
      order
    ),
  };
}

export function mapModuleWithFoldersToTreeNode(
  module: ModuleWithFoldersResponse,
  sort?: ModuleTreeSortField,
  order?: ModuleTreeSortOrder
): DirectoryTreeNode {
  const nodeId = `module-${module.moduleId}`;

  return {
    id: nodeId,
    name: module.moduleName,
    kind: 'module',
    moduleId: module.moduleId,
    ...(module.holdsMatrixLinks === true ? { holdsMatrixLinks: true as const } : {}),
    details: {
      ...buildDetails(module.moduleName, module),
      iconId: resolveIconId(module.iconId),
    },
    children: sortTreeNodes(
      [
        ...module.folders.map((folder) =>
          mapFolderNodeToTreeNode(folder, module.moduleId, sort, order)
        ),
        ...(module.items ?? []).map((item) => mapDirectoryItemToTreeNode(item, nodeId)),
      ],
      sort,
      order
    ),
  };
}
